// ── 점수 관리 — TM 상담원 월간 점수 (회사별) ─────────────────────────────────
//   자동 항목(계약장수·잡은미팅·계약율)은 실시간 집계로 읽기 전용,
//   화합·기타만 관리자가 입력한다. 기준표는 [점수 기준] 버튼에서 회사별·월별로 수정.
//   2026-08-26 디자인 통일: 직원 관리 기준(designStandard) — 텍스트 헤더 + 검정 CTA.
import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import { useScoreMonth } from './useScoreMonth';
import ScoreEmployeeTable from './ScoreEmployeeTable';
import ScoreCriteriaModal from './ScoreCriteriaModal';
import {
  stdPageTitle, stdPageSubtitle, stdHeaderRow, stdBlackBtn, stdOutlineBtn,
} from '../adminlayout/designStandard';

const COMPANY_LABELS = { PINE: '파인그룹' };

export default function ScoreManagement({ companyCode = 'PINE' }) {
  const toast = useToast();
  const showToast = (msg, type) => (type === 'error' ? toast.error(msg) : toast.success(msg));
  const code = String(companyCode || 'PINE').toUpperCase();
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const s = useScoreMonth({ companyCode: code, showToast });
  const cfg = s.data?.rule?.config;
  const summary = s.data?.summary;
  const canSave = s.pendingCount > 0 && s.invalidCount === 0;

  return (
    <div style={{ minWidth: 0 }}>
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>
            점수 관리 <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>· {COMPANY_LABELS[code] || code}</span>
          </h2>
          <div style={stdPageSubtitle}>TM 월간 점수를 관리합니다.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <button style={stdOutlineBtn} onClick={() => s.moveMonth(-1)}>‹</button>
          <span style={{ fontWeight: 800, minWidth: 108, textAlign: 'center', color: 'var(--text)', fontSize: 13 }}>
            {s.year}년 {s.month}월
          </span>
          <button style={stdOutlineBtn} onClick={() => s.moveMonth(1)}>›</button>
          <button style={stdOutlineBtn} onClick={() => setCriteriaOpen(true)} disabled={!cfg}>점수 기준</button>
        </div>
      </div>

      {/* 적용 중인 기준 한 줄 — 왜 이 점수인지 매번 모달을 열지 않아도 되게 */}
      {cfg && (
        <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.8 }}>
          {(cfg.items || []).filter((i) => i.mode === 'auto').map((i) => {
            const base = i.base || {};
            return <span key={i.key} style={{ marginRight: 14 }}>{i.label} <b style={{ color: 'var(--text-sub)' }}>{Number(base.value).toLocaleString('ko-KR')}{i.unit}={base.points}점</b></span>;
          })}
          <span style={{ marginRight: 14 }}>풀타임 기준선 <b style={{ color: 'var(--text-sub)' }}>주 {cfg.employment.fulltimeMinWeeklyHours}시간</b></span>
          <span>파트 배율 <b style={{ color: 'var(--text-sub)' }}>{cfg.employment.scales.parttime}</b></span>
          <span style={{ marginLeft: 10, fontSize: 11 }}>(적용 {s.data.rule.effectiveFromMonth}~)</span>
        </div>
      )}

      {summary && (
        <div style={{ marginBottom: 14, display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 13 }}>
          <span style={{ color: 'var(--text-muted)' }}>대상 <b style={{ color: 'var(--text)' }}>{summary.targetCount}명</b></span>
          <span style={{ color: 'var(--text-muted)' }}>평균 <b style={{ color: 'var(--text)' }}>{summary.averageScore ?? '-'}점</b></span>
          <span style={{ color: 'var(--text-muted)' }}>화합·기타 입력 <b style={{ color: 'var(--text)' }}>{summary.manualFilledCount}명</b></span>
          {summary.unclassifiedCount > 0 && (
            <span style={{ color: '#dc2626', fontWeight: 700 }}>근무유형 미설정 {summary.unclassifiedCount}명</span>
          )}
        </div>
      )}

      {s.loading && <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>불러오는 중...</div>}
      {!s.loading && s.data?.enabled === false && (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>
          이 회사에는 적용할 점수 기준표가 없습니다.
        </div>
      )}
      {!s.loading && s.data?.enabled && (
        <>
          <ScoreEmployeeTable
            rows={s.data.rows}
            manualKeys={s.manualKeys}
            valueOf={s.valueOf}
            isManualSet={s.isManualSet}
            patchDraft={s.patchDraft}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 12 }}>
            {s.invalidCount > 0 && (
              <span style={{ color: '#dc2626', fontSize: 12, fontWeight: 700 }}>범위를 벗어난 값 {s.invalidCount}개</span>
            )}
            <button
              onClick={s.save}
              disabled={s.saving || s.pendingCount === 0 || s.invalidCount > 0}
              style={canSave
                ? { ...stdBlackBtn, padding: '8px 16px' }
                : { ...stdOutlineBtn, padding: '8px 16px', color: 'var(--text-muted)', cursor: 'default' }}
            >
              {s.saving ? '저장 중...' : `저장${s.pendingCount > 0 ? ` (${s.pendingCount}건)` : ''}`}
            </button>
          </div>
        </>
      )}

      {criteriaOpen && cfg && (
        <ScoreCriteriaModal
          open={criteriaOpen}
          onClose={() => setCriteriaOpen(false)}
          onSaved={s.reload}
          companyCode={code}
          config={cfg}
          effectiveFromMonth={s.data.rule.effectiveFromMonth}
          defaultMonth={`${s.year}-${String(s.month).padStart(2, '0')}`}
          showToast={showToast}
        />
      )}
    </div>
  );
}
